import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { roadmapService } from '../services/roadmapService';
import { assessmentService } from '../services/assessmentService';
import RoadmapTimeline from '../components/Roadmap/RoadmapTimeline';
import BudgetBreakdown from '../components/Roadmap/BudgetBreakdown';
import MilestoneTracker from '../components/Roadmap/MilestoneTracker';
import RiskAssessment from '../components/Roadmap/RiskAssessment';
import PathComparison from '../components/Roadmap/PathComparison';
import LeadCapture from '../components/CallToAction/LeadCapture';
import LoadingSpinner from '../components/Layout/LoadingSpinner';
import RoadmapPDFExportV2 from '../components/Roadmap/RoadmapPDFExportV2';
import {
  ArrowLeftIcon,
  ShareIcon
} from '@heroicons/react/24/outline';

const RoadmapPage: React.FC = () => {
  const { roadmapId } = useParams<{ roadmapId: string }>();
  const [selectedPath, setSelectedPath] = useState<'diy' | 'partnership' | null>(null);

  const { data: roadmap, isLoading: roadmapLoading, error: roadmapError } = useQuery(
    ['roadmap', roadmapId],
    () => roadmapService.getRoadmap(roadmapId!),
    {
      enabled: !!roadmapId,
      retry: 2,
    }
  );

  // Fetch assessment data for PDF export
  const { data: assessmentData, isLoading: assessmentLoading } = useQuery(
    ['assessment', roadmap?.assessmentId],
    () => assessmentService.getResults(roadmap!.assessmentId),
    {
      enabled: !!roadmap?.assessmentId,
      retry: 2,
    }
  );

  const isLoading = roadmapLoading || assessmentLoading;
  const error = roadmapError;

  // Merge roadmap and assessment data for PDF export
  const completeRoadmapData = roadmap && assessmentData ? {
    ...roadmap,
    companyName: assessmentData.companyName,
    sector: assessmentData.sector,
    aiReadinessScore: assessmentData.aiReadinessScore,
    breakdown: assessmentData.breakdown,
  } : roadmap;

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Roadmap Implementazione AI Agent',
        text: `Roadmap per ${roadmap?.selectedRecommendation?.name}`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copiato negli appunti!');
    }
  };

  const handleApprove = () => {
    if (!roadmapId) return;
    roadmapService.updateRoadmapStatus(roadmapId, 'approved')
      .then(() => {
        alert('Roadmap approvata! Ti contatteremo presto per i prossimi passi.');
      })
      .catch((error) => {
        console.error('Error approving roadmap:', error);
        alert('Errore nell\'approvazione. Riprova più tardi.');
      });
  };

  const handleRequestChanges = () => {
    alert('Per richiedere modifiche, contattaci a support@aiadviser.it con i dettagli delle modifiche desiderate.');
  };

  const handlePathSelection = (path: 'diy' | 'partnership') => {
    setSelectedPath(path);
    
    // Scroll to appropriate section
    if (path === 'partnership') {
      // Scroll to lead capture form
      setTimeout(() => {
        const element = document.getElementById('lead-capture');
        element?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      // For DIY path, maybe show additional resources or redirect
      alert('Perfetto! Ti invieremo una guida completa per l\'implementazione fai-da-te via email.');
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Caricamento roadmap..." />;
  }

  if (error || !roadmap) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Errore nel caricamento della roadmap
          </h1>
          <p className="text-gray-600 mb-6">
            Non siamo riusciti a trovare la roadmap richiesta.
          </p>
          <Link
            to="/assessment"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Torna all'assessment
          </Link>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount: number | undefined | null) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return 'Da definire';
    }
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <Link
                to={`/results/${roadmap.assessmentId}`}
                className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-2"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-1" />
                Torna ai risultati
              </Link>
              <h1 className="text-4xl font-bold text-gray-900">
                Roadmap Implementazione
              </h1>
              <p className="mt-2 text-lg text-gray-600">
                {roadmap.selectedRecommendation?.name || 'AI Agent Implementation'}
              </p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={handleShare}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <ShareIcon className="h-4 w-4 mr-2" />
                Condividi
              </button>
              {completeRoadmapData && <RoadmapPDFExportV2 roadmap={completeRoadmapData} />}
            </div>
          </div>

          {/* Status and key metrics */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg p-4 border">
              <div className="text-sm text-gray-500">Budget Totale</div>
              <div className="text-2xl font-bold text-gray-900">
                {formatCurrency(roadmap.estimatedTotalCost)}
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 border">
              <div className="text-sm text-gray-500">ROI Stimato</div>
              <div className="text-2xl font-bold text-green-600">
                {roadmap.estimatedROI}
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 border">
              <div className="text-sm text-gray-500">Timeline</div>
              <div className="text-2xl font-bold text-gray-900">180 giorni</div>
            </div>
            <div className="bg-white rounded-lg p-4 border">
              <div className="text-sm text-gray-500">Status</div>
              <div className="text-2xl font-bold text-blue-600 capitalize">
                {roadmap.status}
              </div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left column - Timeline and Milestones */}
          <div className="lg:col-span-2 space-y-8">
            <RoadmapTimeline timeline={roadmap.timeline} />
            <MilestoneTracker milestones={roadmap.milestones} />
          </div>

          {/* Right column - Budget and Risks */}
          <div className="space-y-8">
            <BudgetBreakdown budgetBreakdown={roadmap.budgetBreakdown} />
            <RiskAssessment risks={roadmap.riskAssessment} />
            
            {/* Recommendation details */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                AI Agent Selezionato
              </h3>
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-gray-500">Nome:</span>
                  <p className="font-medium">{roadmap.selectedRecommendation?.name}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Descrizione:</span>
                  <p className="text-sm text-gray-700">{roadmap.selectedRecommendation?.description}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-500">Complessità:</span>
                    <p className="font-medium">{roadmap.selectedRecommendation?.complexity || 'Medium'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Time to Value:</span>
                    <p className="font-medium">{roadmap.selectedRecommendation?.timeToValue || '60-90 giorni'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Path Comparison */}
        <PathComparison 
          originalBudget={roadmap.estimatedTotalCost || 75000}
          selectedAgent={roadmap.selectedRecommendation?.name}
          agentComplexity={roadmap.selectedRecommendation?.complexity}
          onSelectPath={handlePathSelection}
        />

        {/* Lead Capture - Show only if partnership path is selected */}
        {selectedPath === 'partnership' && (
          <div id="lead-capture">
            <LeadCapture 
              assessmentId={roadmap.assessmentId}
              roadmapId={roadmapId}
              selectedAgent={roadmap.selectedRecommendation?.name}
              estimatedBudget={roadmap.estimatedTotalCost}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default RoadmapPage;